/**
 * Load something once, show what happened, and be able to ask again.
 *
 * Every screen here does the same three things with a list: fetch it, render it,
 * and refetch after a write. This is that, so the screens read as what they show
 * rather than as `useEffect` bookkeeping. The `reload` it returns is a stable
 * function a button can call, and the load runs on mount and whenever `deps`
 * change.
 *
 * The `cancelled` flag matters more than it looks: a screen that unmounts while a
 * request is in flight would otherwise set state on a component that is gone -
 * which in React 18 is a warning now and a bug later.
 */

import { useCallback, useEffect, useState } from 'react';

export type Async<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'error'; message: string; detail?: unknown };

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[] = []): [Async<T>, () => void] {
  const [state, setState] = useState<Async<T>>({ status: 'loading' });
  const [generation, setGeneration] = useState(0);

  // The loader is intentionally not a dependency: callers write it inline, and a
  // new function identity on every render would refetch forever. `deps` is the
  // contract for "this changed, load again".
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadStable = useCallback(load, deps);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    loadStable()
      .then((value) => {
        if (!cancelled) setState({ status: 'ready', value });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'something went wrong',
          detail: error,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [loadStable, generation]);

  const reload = useCallback(() => setGeneration((value) => value + 1), []);
  return [state, reload];
}
